import React, { useState, useEffect } from "react";
import { Button, Card, Form, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import InputComponent from "components/shared/InputComponent";
import Select from "components/shared/Select";
import { createAIStudySet } from "services/Teacher/Vocabulary.service"; // Assuming this API exists
import { getAllSubject } from "services/Teacher/Exercise.service"; // Fetch languages API
import { useNavigate } from "react-router-dom";
import { GetUserRoles } from "utils/auth.utils";
import { CREDIT_USAGE } from "utils/constants";
import { useAuth } from "utils/AuthContext";
import PageHeading from "components/shared/PageHeading";
import { useTranslation } from "react-i18next";
import useAutoSave from "hooks/useAutoSave";
import AutoSaveIndicator from "components/shared/AutoSaveIndicator";
import { getFormDraft, patchFormDraft, deleteFormDraft } from "services/formDraft.service";

const AiGeneratedVocabulary = () => {
  const { t } = useTranslation();
  const [theme, setTheme] = useState("");
  const [difficultyLevel, setDifficultyLevel] = useState("");
  const [studySetSize, setStudySetSize] = useState(5);
  const userRole = GetUserRoles();
  const [sourceLanguage, setSourceLanguage] = useState("");
  const [translationLanguage, setTranslationLanguage] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true); // To handle form validation
  const [errors, setErrors] = useState({});
  const [userPrompt, setUserPrompt] = useState(""); // New state for user prompt
  const navigate = useNavigate();
  const { userRoles } = useAuth();

  // Draft auto-save for the AI vocabulary-generation form — persists inputs
  // server-side after typing stops and restores them on return. Never triggers AI.
  const vocabDraftKey = "vocab-draft-ai";

  // Autosave must stay OFF until the draft has been fetched and applied.
  // Otherwise a save can fire during the mount race and overwrite the draft
  // with whatever default is momentarily displayed.
  const [hydrated, setHydrated] = useState(false);

  // Fetch subjects when the component mounts
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await getAllSubject();
        const formattedSubjects = response.data.results.subject.map((subject) => ({
          value: subject._id,
          label: subject.title,
        }));
        setSubjects(formattedSubjects);
        // Default to the first/second subjects ONLY as a fallback — never
        // override a value the draft-restore effect may have already set (this
        // effect and restore race on mount; without the guard, the default
        // clobbers a restored language selection on refresh).
        setSourceLanguage((prev) => prev || formattedSubjects[0]?.value || "");
        setTranslationLanguage((prev) => prev || formattedSubjects[1]?.value || "");
      } catch (error) {
        console.error("Error fetching subjects:", error);
      }
    };

    fetchSubjects();
  }, []);

  // Restore any saved draft on mount, then enable autosave.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getFormDraft(vocabDraftKey);
        const d = res?.data?.result;
        if (!cancelled && d && typeof d === "object" && Object.keys(d).length) {
          if (typeof d.userPrompt === "string") setUserPrompt(d.userPrompt);
          if (typeof d.sourceLanguage === "string") setSourceLanguage(d.sourceLanguage);
          if (typeof d.translationLanguage === "string")
            setTranslationLanguage(d.translationLanguage);
          if (typeof d.difficultyLevel === "string")
            setDifficultyLevel(d.difficultyLevel);
          if (d.studySetSize != null) setStudySetSize(d.studySetSize);
        }
      } catch (e) {
        /* ignore */
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    status: vocabAutoSaveStatus,
    lastSavedAt: vocabAutoSaveAt,
    isOnline: vocabAutoSaveOnline,
  } = useAutoSave(
    {
      userPrompt,
      sourceLanguage,
      translationLanguage,
      difficultyLevel,
      studySetSize,
    },
    (changed) => patchFormDraft(vocabDraftKey, changed),
    { enabled: hydrated }
  );

  // Handle the form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
   
    
    let validationErrors = {};

    // Validate all required fields
    

    if (!sourceLanguage) {
      validationErrors.sourceLanguage = t("Source language is required");
    }

    if (!translationLanguage) {
      validationErrors.translationLanguage = t("Target language is required");
    }

    if (sourceLanguage === translationLanguage) {
      validationErrors.languageMismatch = t("Source and Target languages cannot be the same");
    }

    if (!difficultyLevel) {
      validationErrors.difficultyLevel = t("Difficulty level is required");
    }

    if (studySetSize < 5 || studySetSize > 20) {
      validationErrors.studySetSize = t("Study Set Size should be between 5 and 20");
    }
    if (!userPrompt) {
      validationErrors.userPrompt = t("User prompt is required");
    }
    if (userPrompt.length > 150) {
      validationErrors.userPrompt = t("User prompt should not exceed 150 characters");
    }

    setErrors(validationErrors);

    // If there are validation errors, stop the form submission
    if (Object.keys(validationErrors).length > 0) {
      setIsFormValid(false);
      return;
    }

    const payload = {
      difficultyLevel,
      studySetSize,
      sourceLanguage,
      translationLanguage,
      userPrompt,
      isLibraryStudySet: userRole === "superUser" ? true : false,
    };

    const toastId = toast.loading(t("Generating Study Set..."));

    try {
      setLoading(true);
      const response = await createAIStudySet(payload);
      toast.update(toastId, {
        render: t("Study set generated successfully!"),
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      window.dispatchEvent(new Event("user-credits"));
      await deleteFormDraft(vocabDraftKey).catch(() => {});
      navigate(`/vocabulary/${response.data?.result?.studySet._id}`);
    } catch (error) {
      console.error("Error generating study set:", error);
      toast.update(toastId, {
        render: error?.response?.data?.message || error?.response?.data?.error || t("Error generating Study set."),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Predefined Themes
  const themeOptions = [
    { value: "", label: "Select Theme" },
    { value: "Animals", label: "Animals" },
    { value: "Technology", label: "Technology" },
    { value: "Food", label: "Food" },
    { value: "Nature", label: "Nature" },
    { value: "Sports", label: "Sports" },
  ];

  return (
    <>
      <Card className="border-0 shadow p-3">
        <div className="d-flex justify-content-end mb-2">
          <AutoSaveIndicator draft
            status={vocabAutoSaveStatus}
            lastSavedAt={vocabAutoSaveAt}
            isOnline={vocabAutoSaveOnline}
          />
        </div>
        <Form onSubmit={handleSubmit}>
        <Form.Group controlId="userPrompt">
            <Form.Label>Topic</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              maxLength={150}
              isInvalid={!!errors.userPrompt}
              placeholder="Provide a custom prompt for the AI to refine the study set (max 150 characters)"
            />
            {errors.userPrompt && <p style={{ color: "red" }}>{errors.userPrompt}</p>}
          </Form.Group>
          

          <Select
            heading={"Subject"}
            value={sourceLanguage}
            onChange={setSourceLanguage}
            options={subjects}
            ariaLabel={t("Select source language")}
            required
            isInvalid={!!errors.sourceLanguage}
          />
          {errors.sourceLanguage && <p style={{ color: "red" }}>{errors.sourceLanguage}</p>}

          <Select
            heading={"Translation"}
            value={translationLanguage}
            onChange={setTranslationLanguage}
            options={subjects}
            ariaLabel={t("Select target language")}
            required
            isInvalid={!!errors.translationLanguage || !!errors.languageMismatch}
          />
          {errors.translationLanguage && <p style={{ color: "red" }}>{errors.translationLanguage}</p>}
          {errors.languageMismatch && <p style={{ color: "red" }}>{errors.languageMismatch}</p>}

          <Select
            heading={t("Difficulty Level")}
            value={difficultyLevel}
            onChange={setDifficultyLevel}
            options={[
              { value: "", label: t("Select Difficulty") },
              { value: "Beginner", label: t("Beginner") },
              { value: "Elementary", label: t("Elementary") },
              { value: "Intermediate", label: t("Intermediate") },
              { value: "Advanced", label: t("Advanced") },
              { value: "Expert", label: t("Expert") },
            ]}
            ariaLabel="Select difficulty level"
            required
            isInvalid={!!errors.difficultyLevel}
          />
          {errors.difficultyLevel && <p style={{ color: "red" }}>{errors.difficultyLevel}</p>}

          {/* Study Set Size as Slider */}
          <Col xs={12} lg={6}>
            <Form.Group controlId="studySetSize">
              <Form.Label>{t("Study Set Size")}: {studySetSize}</Form.Label>
              <Form.Range
                min={5}
                max={20}
                value={studySetSize}
                onChange={(e) => setStudySetSize(e.target.value)}
              />
            </Form.Group>
            {errors.studySetSize && <p style={{ color: "red" }}>{errors.studySetSize}</p>}
          </Col>

          {/* New User Prompt Textarea */}
          

          <div className="d-flex justify-content-end mt-3"  style={{display:"flex", alignItems:"center", gridGap:"10px", flexWrap:"wrap"}}>
            {CREDIT_USAGE && userRoles !== "superUser" && <span style={{color:"#757575"}}>*Note: <span style={{color:"#e74c3c", fontWeight:"500"}}>{CREDIT_USAGE?.Vocabulary} credits</span> required to generate</span>}
            <Button type="submit" disabled={loading} className="button-style border-0 py-2 px-4" >
              Generate
            </Button>
          </div>
        </Form>
      </Card>
    </>
  );
};

export default AiGeneratedVocabulary;
